import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useAuth } from '../../hooks/useAuth'
import { usePagination } from '../../hooks/usePagination'
import { supportService } from '../../services/supportService'
import { SupportTicket, SupportTicketStatus, SupportTicketType } from '../../types/support'
import { Pagination } from '../../components/ui/Pagination'
import {
  PageHeader,
  PageMeta,
  PageEyebrow,
  PageTitle,
  TableWrapper,
  StyledTable,
  THead,
  TH,
  EmptyState,
  TableSkeleton,
} from '../../components/ui/Table'

// ─── Styles ───────────────────────────────────────────────
const Filters = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`

const Select = styled.select`
  padding: 7px 12px;
  border-radius: 8px;
  border: 1px solid #e2d9f3;
  background: white;
  font-size: 13px;
  color: #3d1a0a;
  cursor: pointer;
  outline: none;

  &:focus {
    border-color: #9b6bb5;
  }
`

const TR = styled.tr`
  cursor: pointer;
  &:hover td {
    background: #faf7fc;
  }
`

const TD = styled.td`
  padding: 12px 16px;
  font-size: 13px;
  color: #3d2460;
  border-bottom: 1px solid #f0ebf8;
  vertical-align: middle;
`

const StatusBadge = styled.span<{ $status: SupportTicketStatus }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  background: ${({ $status }) => {
    if ($status === 'open') return '#fce4b4'
    if ($status === 'in_progress') return '#dbeafe'
    if ($status === 'resolved') return '#dcfce7'
    return '#f1f5f9'
  }};
  color: ${({ $status }) => {
    if ($status === 'open') return '#92400e'
    if ($status === 'in_progress') return '#1e40af'
    if ($status === 'resolved') return '#166534'
    return '#475569'
  }};
`

const PriorityDot = styled.span<{ $priority: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  background: ${({ $priority }) => {
    if ($priority === 'urgent') return '#ef4444'
    if ($priority === 'high') return '#f97316'
    if ($priority === 'medium') return '#eab308'
    return '#94a3b8'
  }};
`

const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  background: #f0ebf8;
  color: #6b21a8;
`

const UnreadDot = styled.span`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #e8724a;
  margin-right: 6px;
  flex-shrink: 0;
`

const SubjectCell = styled.div`
  display: flex;
  align-items: center;
`

const LinearBadge = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #5e6ad2;
  text-decoration: none;
  padding: 2px 6px;
  border-radius: 6px;
  background: #eef0ff;

  &:hover {
    background: #e0e3ff;
  }
`

const TYPE_LABELS: Record<SupportTicketType, string> = {
  bug: '🐛 Bug',
  suggestion: '💡 Suggestion',
  error: '⚠️ Error',
  other: '📌 Other',
}

// ─── Component ────────────────────────────────────────────
export function SupportPage() {
  const { token } = useAuth()
  const navigate = useNavigate()
  const svc = useMemo(() => supportService(token), [token])
  const { page, perPage, setPage } = usePagination()

  const [items, setItems] = useState<SupportTicket[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await svc.list(page, perPage, statusFilter || undefined, typeFilter || undefined)
      setItems(res.items)
      setTotal(res.total)
    } finally {
      setLoading(false)
    }
  }, [svc, page, perPage, statusFilter, typeFilter])

  useEffect(() => {
    load()
  }, [load])

  return (
    <>
      <PageHeader>
        <PageMeta>
          <PageEyebrow>Support</PageEyebrow>
          <PageTitle>Tickets</PageTitle>
        </PageMeta>
      </PageHeader>

      <Filters>
        <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </Select>
        <Select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All types</option>
          <option value="bug">Bug</option>
          <option value="suggestion">Suggestion</option>
          <option value="error">Error</option>
          <option value="other">Other</option>
        </Select>
      </Filters>

      <TableWrapper>
        <StyledTable>
          <THead>
            <tr>
              <TH>Subject</TH>
              <TH>Type</TH>
              <TH>Status</TH>
              <TH>Priority</TH>
              <TH>User</TH>
              <TH>Platform</TH>
              <TH>Msgs</TH>
              <TH>Linear</TH>
              <TH>Created</TH>
            </tr>
          </THead>
          <tbody>
            {loading && <TableSkeleton cols={9} rows={8} />}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={9}>
                  <EmptyState message="No tickets found." />
                </td>
              </tr>
            )}
            {!loading &&
              items.map(ticket => (
                <TR key={ticket.id} onClick={() => navigate(`/support/${ticket.id}`)}>
                  <TD>
                    <SubjectCell>
                      {!ticket.readBySupport && <UnreadDot />}
                      {ticket.subject}
                    </SubjectCell>
                  </TD>
                  <TD>
                    <TypeBadge>{TYPE_LABELS[ticket.type]}</TypeBadge>
                  </TD>
                  <TD>
                    <StatusBadge $status={ticket.status}>
                      {ticket.status.replace('_', ' ')}
                    </StatusBadge>
                  </TD>
                  <TD>
                    <PriorityDot $priority={ticket.priority} />
                    {ticket.priority}
                  </TD>
                  <TD>{ticket.user?.email ?? '—'}</TD>
                  <TD>{ticket.platform}</TD>
                  <TD>{ticket.messageCount}</TD>
                  <TD>
                    {ticket.isEscalated && ticket.linearIssueUrl ? (
                      <LinearBadge
                        href={ticket.linearIssueUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                      >
                        ↗ Linear
                      </LinearBadge>
                    ) : (
                      '—'
                    )}
                  </TD>
                  <TD>{new Date(ticket.createdAt).toLocaleDateString()}</TD>
                </TR>
              ))}
          </tbody>
        </StyledTable>
      </TableWrapper>

      {total > perPage && (
        <Pagination page={page} perPage={perPage} total={total} onChange={setPage} />
      )}
    </>
  )
}
